import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowDown, Users, BarChart3 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import SystemCheckBlock from "@/components/admin/SystemCheckBlock";
import LoadMonitorBlock from "@/components/admin/LoadMonitorBlock";
import McQueueBlock from "@/components/admin/McQueueBlock";
import TrialStatsBlock, { TRIAL_STATS_ID } from "@/components/admin/TrialStatsBlock";
import InvoiceQueueBlock from "@/components/admin/InvoiceQueueBlock";
import DiscountCodesBlock from "@/components/admin/DiscountCodesBlock";
import UsersBlock from "@/components/admin/UsersBlock";
import AiUsageBlock from "@/components/admin/AiUsageBlock";
import CronLogBlock from "@/components/admin/CronLogBlock";
import ManualBlock from "@/components/admin/ManualBlock";
import PricingBlock from "@/components/admin/PricingBlock";
import AdminShortcuts from "@/components/admin/AdminShortcuts";
import HelpSessionsBlock from "@/components/admin/HelpSessionsBlock";

export default function AdminPage() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <AppLayout>
      <div className="mb-8" data-testid="admin-page">
        <div className="stat-label">Beheer · Systeem</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-1">Beheer</h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Systeemcheck, belastingsmonitor, facturen, gebruikers, geplande taken, kortingscodes, handleiding en helpchat. Alleen zichtbaar voor beheerders.</p>
      </div>
      <div className="space-y-8">
        <div className="grid md:grid-cols-3 gap-6">
        <Link to="/uitnodigingen" data-testid="admin-invites-card"
          className="block bg-card border-2 border-foreground heavy-shadow p-5 hover:bg-primary hover:text-primary-foreground transition-colors group">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border-2 border-foreground bg-secondary text-secondary-foreground flex items-center justify-center shrink-0 group-hover:border-primary-foreground">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <div className="stat-label group-hover:text-primary-foreground/80">Klanten &amp; toegang</div>
                <div className="font-display font-black text-2xl uppercase tracking-tight">Uitnodigingen beheren</div>
                <div className="text-xs text-muted-foreground group-hover:text-primary-foreground/80 mt-0.5">
                  Uitnodigingscodes aanmaken, mailen en intrekken · /uitnodigingen
                </div>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 shrink-0" />
          </div>
        </Link>
        <a href={`#${TRIAL_STATS_ID}`} data-testid="admin-stats-card"
          onClick={(e) => { e.preventDefault(); document.getElementById(TRIAL_STATS_ID)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
          className="block bg-card border-2 border-foreground heavy-shadow p-5 hover:bg-secondary hover:text-secondary-foreground transition-colors group">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border-2 border-foreground bg-primary text-primary-foreground flex items-center justify-center shrink-0 group-hover:border-secondary-foreground">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <div className="stat-label group-hover:text-secondary-foreground/80">Proefstarts &amp; omzettingen</div>
                <div className="font-display font-black text-2xl uppercase tracking-tight">Statistieken</div>
                <div className="text-xs text-muted-foreground group-hover:text-secondary-foreground/80 mt-0.5">
                  Proefstarts per week met schuifbare tijdbalk · verderop op deze pagina
                </div>
              </div>
            </div>
            <ArrowDown className="w-6 h-6 shrink-0" />
          </div>
        </a>
        <AdminShortcuts />
        </div>
        <TrialStatsBlock />
        <SystemCheckBlock />
        <LoadMonitorBlock />
        <McQueueBlock />
        <InvoiceQueueBlock />
        <UsersBlock />
        <AiUsageBlock />
        <CronLogBlock />
        <PricingBlock />
        <DiscountCodesBlock />
        <ManualBlock />
        <HelpSessionsBlock />
      </div>
    </AppLayout>
  );
}
